import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { clinics as clinicsApi, patients as patientsApi } from '../api/client';
import { DEFAULT_PROCEDURES } from '../lib/default-procedures';
import {
  generateId, formatCPF,
  createVersionSnapshot, MAX_VERSIONS,
} from '../lib/consent-utils';
import { getDisplayName } from '../lib/profession';

// --- Legacy localStorage helpers (from original App.jsx) ---

const STORAGE_KEY = 'ophth-consent-app';
const LEGACY_PROCEDURES_KEY = 'ophth-consent-procedures';
const LEGACY_CLINIC_KEY = 'ophth-consent-clinic';

const DEFAULT_USER = { name: '', profession: '', crm: '', type: 'doctor' };

const createClinic = (name = '', overrides = {}) => ({
  id: generateId(), name, address: '', phone: '', logoUrl: '',
  templates: { ...DEFAULT_PROCEDURES }, ...overrides,
});

const createDefaultAppState = () => {
  const clinic1 = createClinic('');
  return { user: { ...DEFAULT_USER }, clinics: [clinic1], activeClinicId: clinic1.id };
};

const migrateLegacyData = () => {
  const legacyProcedures = localStorage.getItem(LEGACY_PROCEDURES_KEY);
  const legacyClinic = localStorage.getItem(LEGACY_CLINIC_KEY);
  if (!legacyProcedures && !legacyClinic) return null;
  let templates = DEFAULT_PROCEDURES;
  let clinicInfo = { name: '', address: '', phone: '', logoUrl: '' };
  try { if (legacyProcedures) templates = JSON.parse(legacyProcedures); } catch {}
  try { if (legacyClinic) clinicInfo = JSON.parse(legacyClinic); } catch {}
  const migratedClinic = {
    id: generateId(), name: clinicInfo.name || 'Minha Clínica',
    address: clinicInfo.address || '', phone: clinicInfo.phone || '',
    logoUrl: clinicInfo.logoUrl || '', templates,
  };
  const migratedState = { user: { ...DEFAULT_USER }, clinics: [migratedClinic], activeClinicId: migratedClinic.id };
  localStorage.removeItem(LEGACY_PROCEDURES_KEY);
  localStorage.removeItem(LEGACY_CLINIC_KEY);
  return migratedState;
};

const loadAppState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const state = JSON.parse(stored);
      if (!state.activeClinicId || !state.clinics.find(c => c.id === state.activeClinicId)) {
        state.activeClinicId = state.clinics[0]?.id || null;
      }
      return state;
    }
    const migrated = migrateLegacyData();
    if (migrated) { saveAppState(migrated); return migrated; }
    const d = createDefaultAppState(); d.activeClinicId = d.clinics[0].id; return d;
  } catch {
    const d = createDefaultAppState(); d.activeClinicId = d.clinics[0].id; return d;
  }
};

const saveAppState = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
};

// --- Version history localStorage helpers ---

export const getStoredVersions = (clinicId) => {
  try { const raw = localStorage.getItem(`templateVersions_${clinicId}`); return raw ? JSON.parse(raw) : {}; }
  catch { return {}; }
};

export const saveStoredVersions = (clinicId, versions) => {
  try { localStorage.setItem(`templateVersions_${clinicId}`, JSON.stringify(versions)); }
  catch (e) { console.error('Failed to save version history:', e); }
};

// --- Context ---

const ConsentFlowContext = createContext(null);

export function ConsentFlowProvider({ children }) {
  const { user: authUser, currentClinic, clinics, updateProfile } = useAuth();

  // Legacy appState (user profile cache)
  const [appState, setAppState] = useState(loadAppState);
  useEffect(() => { saveAppState(appState); }, [appState]);
  useEffect(() => {
    if (authUser?.name) {
      const crmFormatted = authUser.crm && authUser.crmState
        ? `CRM ${authUser.crmState} ${authUser.crm}` : authUser.crm || '';
      setAppState(prev => ({
        ...prev,
        user: { ...prev.user, name: authUser.name, profession: authUser.profession || prev.user.profession, crm: crmFormatted || prev.user.crm },
      }));
    }
  }, [authUser?.id]);

  const updateUser = (field, value) => {
    setAppState(prev => ({ ...prev, user: { ...prev.user, [field]: value } }));
  };

  const saveProfile = async () => {
    try {
      const updates = { name: appState.user.name, profession: appState.user.profession || undefined };
      const crmMatch = appState.user.crm?.match(/(?:CRM\s+)?(\w{2})\s+(\d+)/i);
      if (crmMatch) { updates.crmState = crmMatch[1].toUpperCase(); updates.crm = crmMatch[2]; }
      await updateProfile(updates);
    } catch (err) { console.error('Failed to save profile:', err); }
  };

  // Templates (procedures) state
  const [procedures, setProceduresState] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const setProcedures = useCallback((updater) => {
    setProceduresState(prev => typeof updater === 'function' ? updater(prev) : updater);
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!currentClinic?.id) { setProceduresState({}); return; }
      setLoadingTemplates(true);
      try {
        const token = localStorage.getItem('supabase_token');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/clinics/${currentClinic.id}/templates`, {
          headers: { 'Authorization': `Bearer ${token}`, 'X-Clinic-Id': currentClinic.id },
        });
        if (response.ok) {
          const templates = await response.json();
          const storedVersions = getStoredVersions(currentClinic.id);
          const obj = {};
          templates.forEach(t => {
            obj[t.id] = {
              id: t.id, name: t.name, description: t.description,
              details: t.details || [], risks: t.risks || [],
              preOp: t.preOp || [], postOp: t.postOp || [],
              recovery: t.recovery, category: t.category,
              versions: storedVersions[t.id] || [],
            };
          });
          setProceduresState(obj);
        } else { setProceduresState({}); }
      } catch { setProceduresState({}); }
      finally { setLoadingTemplates(false); }
    };
    fetchTemplates();
  }, [currentClinic?.id]);

  // Consent builder state
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [patientData, setPatientData] = useState({ patientId: null, name: '', cpf: '', email: '', phone: '', consultDate: '' });
  const [patientMode, setPatientMode] = useState('existing');
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [patientSearching, setPatientSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showSigningModal, setShowSigningModal] = useState(false);

  // Clinic settings (form state for modal)
  const [clinicSettings, setClinicSettingsState] = useState({ name: '', address: '', city: '', state: '', phone: '', logoUrl: '' });
  const setClinicSettings = useCallback((updater) => { setClinicSettingsState(updater); }, []);
  const updateClinicSetting = useCallback((field, value) => {
    setClinicSettingsState(prev => ({ ...prev, [field]: value }));
  }, []);

  // Sync clinic settings from current clinic data
  useEffect(() => {
    if (currentClinic) {
      setClinicSettingsState({
        name: currentClinic.name || '',
        address: currentClinic.address || '',
        city: currentClinic.city || '',
        state: currentClinic.state || '',
        phone: currentClinic.phone || '',
        logoUrl: currentClinic.logoUrl || '',
      });
    }
  }, [currentClinic?.id]);

  // Clear builder state on clinic switch
  useEffect(() => {
    setSelectedProcedures([]);
  }, [currentClinic?.id]);

  // Helpers
  const toggleProcedure = useCallback((procId) => {
    setSelectedProcedures(prev => prev.includes(procId) ? prev.filter(id => id !== procId) : [...prev, procId]);
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setPatientData(prev => ({ ...prev, [field]: value }));
  }, []);

  const getMergedContent = useCallback(() => {
    if (selectedProcedures.length === 0) return null;
    const procs = selectedProcedures.map(id => procedures[id]).filter(Boolean);
    if (procs.length === 0) return null;
    const allRisks = new Set(); procs.forEach(p => p.risks.forEach(r => allRisks.add(r)));
    const allPreOp = new Set(); procs.forEach(p => p.preOp.forEach(i => allPreOp.add(i)));
    const allPostOp = new Set(); procs.forEach(p => p.postOp.forEach(i => allPostOp.add(i)));
    const recoveryTimes = procs.map(p => p.recovery);
    return {
      procedureNames: procs.map(p => p.name).join(', '),
      descriptions: procs.map(p => ({ name: p.name, description: p.description, details: p.details })),
      risks: Array.from(allRisks), preOp: Array.from(allPreOp), postOp: Array.from(allPostOp),
      recovery: recoveryTimes[recoveryTimes.length - 1],
    };
  }, [selectedProcedures, procedures]);

  const generateDocument = useCallback(() => {
    const merged = getMergedContent();
    if (!merged) return '';
    const today = new Date().toLocaleDateString('pt-BR');
    const doctorName = appState.user.name ? getDisplayName(appState.user.name, appState.user.profession) : '[NOME DO MÉDICO]';
    const doctorCRM = appState.user.crm || '[CRM]';

    return `TERMO DE CONSENTIMENTO LIVRE E ESCLARECIDO PARA PROCEDIMENTO CIRÚRGICO

Através do presente TERMO DE CONSENTIMENTO LIVRE E ESCLARECIDO,

Eu, ${patientData.name || '[NOME DO PACIENTE]'}, CPF ${patientData.cpf || '[CPF]'}, formalizo, nos termos do processo informacional verbal realizado no dia ${patientData.consultDate || '[DATA]'} no consultório da Médica ${doctorName}, ${doctorCRM}, o conjunto de informações sobre as vantagens e riscos associados ao procedimento que pretendo contratar, nos termos dos artigos 22 e 24 da Resolução CFM 2.217/2018 (Código de Ética Médica).

Naquela oportunidade fui devidamente informado(a) e esclarecido(a) em linguagem clara e acessível a mim acerca do(s) procedimento(s) de ${merged.procedureNames} a que voluntariamente me submeterei, além de ter tido oportunidade de refletir e sanar todas as minhas dúvidas, estando ciente da natureza da obrigação assumida pelo Médico, bem como dos benefícios do procedimento, além dos riscos e das complicações que poderão ocorrer.

1 – DO CARÁTER REPARADOR E OBRIGAÇÃO DE MEIO

1.1. Todo procedimento médico é reparador e estético ao mesmo tempo, não sendo possível caracterizar nenhum procedimento como "meramente estético". A saúde humana é o conjunto de bem-estar físico, mental e social, sendo que os procedimentos invariavelmente interferem nesse conjunto melhorando e reparando a saúde dos pacientes.

1.2. Também é de ciência do(a) paciente que o médico assume neste ato, e em todos os outros atos médicos, inequivocamente uma obrigação de meio. Não foi prometido pelo médico nenhum resultado.

2 – DO(S) PROCEDIMENTO(S)

${merged.descriptions.map((proc, idx) => `
2.${idx + 1}. ${proc.name}

2.${idx + 1}.1. ${proc.description}

${proc.details.map((detail, detailIdx) => `2.${idx + 1}.${detailIdx + 2}. ${detail}`).join('\n\n')}
`).join('\n')}

3 – PESSOAS COM MAIORES RISCOS

3.1. Todo paciente encontra-se sujeito a riscos na realização de procedimentos cirúrgicos. Todavia, existem pessoas que apresentam risco elevados para determinadas intercorrências, sendo que se encontram entre essas pessoas, principalmente:

(I) Pacientes grávidas e lactantes;
(II) Nódulos de tireoide: bócio coloide;
(III) Uso de imunossupressores;
(IV) Distúrbios de coagulação e uso de anticoagulantes;
(V) Inflamação ou infecção no local a ser tratado;
(VI) Doenças pré-existentes: hipertensão arterial, diabetes e outras doenças crônicas;
(VII) Distúrbios psiquiátricos, depressão, psicose, alcoolismo e dependência química;
(VIII) Doenças nos olhos: glaucoma, olho seco e/ou descolamento de retina;
(IX) Problemas de tireoide;
(X) Pacientes com distúrbios de comportamento.

4 – DOS PRINCIPAIS RISCOS

4.1. As possíveis e principais, mas não únicas, intercorrências previstas na literatura médica para o(s) procedimento(s) realizado(s) são:

${merged.risks.map((risk, idx) => `(${['I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII','XIII','XIV','XV','XVI','XVII','XVIII','XIX','XX','XXI','XXII','XXIII','XXIV','XXV','XXVI','XXVII','XXVIII','XXIX','XXX'][idx] || idx + 1}) ${risk};`).join('\n')}

5 – RECOMENDAÇÕES PRÉ-OPERATÓRIAS

${merged.preOp.map((instr, idx) => `5.${idx + 1}. ${instr};`).join('\n')}

6 – CUIDADOS NO PÓS-OPERATÓRIO

${merged.postOp.map((instr, idx) => `6.${idx + 1}. ${instr};`).join('\n')}

7 – DO RESULTADO

7.1. Foi-me explicado que os benefícios finais do(s) procedimento(s) aparecerão dentro de algumas semanas, podendo levar até ${merged.recovery}. É fundamental seguir todas as orientações médicas para obtenção dos melhores resultados.

8 – DAS DECLARAÇÕES E CONSENTIMENTOS

8.1. AFIRMO ter feito um relato cuidadoso da minha história pessoal e familiar de saúde física e mental.

8.2. COMPROMETO-ME a informar todo e qualquer medicamento/droga utilizado nas últimas 72 (setenta e duas) horas antes da cirurgia.

8.3. DECLARO que nada omiti em relação à minha saúde e que esta declaração passe a fazer parte da minha ficha clínica.

8.4. RECONHEÇO que durante o ato cirúrgico podem surgir situações ou elementos novos que não puderam ser previamente identificados.

8.5. AUTORIZO o registro (foto, som e imagem etc.) dos procedimentos necessários para a(s) cirurgia(s) proposta(s).

8.6. Fui CIENTIFICADO(A) e autorizo a coleta e o tratamento de dados pessoais em decorrência da prestação do serviço médico contratado, nos moldes da Lei 13.709/2018 (LGPD).

9 – AUTORIZAÇÃO DE USO DE IMAGEM

9.1. AUTORIZO a ${doctorName}, ${doctorCRM}, a documentar o meu caso clínico através de fotografias e filmagem em vídeo para fins científicos e educacionais.

( X ) CONSINTO  (  ) RECUSO com a realização do(s) procedimento(s) acima descrito(s).

Brasília (DF), ${today}

______________________________________________
PACIENTE: ${patientData.name || '[NOME DO PACIENTE]'}

______________________________________________
MÉDICO(A) RESPONSÁVEL: ${doctorName}
${doctorCRM}
`;
  }, [getMergedContent, appState.user, patientData]);

  const value = {
    // Legacy appState
    appState, updateUser, saveProfile,
    // Templates
    procedures, setProcedures, loadingTemplates,
    // Builder
    selectedProcedures, setSelectedProcedures, toggleProcedure,
    patientData, setPatientData, handleInputChange,
    patientMode, setPatientMode,
    patientSearch, setPatientSearch,
    patientResults, setPatientResults,
    patientSearching, setPatientSearching,
    selectedPatient, setSelectedPatient,
    showSigningModal, setShowSigningModal,
    // Clinic settings
    clinicSettings, setClinicSettings, updateClinicSetting,
    // Computed
    getMergedContent, generateDocument,
  };

  return (
    <ConsentFlowContext.Provider value={value}>
      {children}
    </ConsentFlowContext.Provider>
  );
}

export function useConsentFlow() {
  const ctx = useContext(ConsentFlowContext);
  if (!ctx) throw new Error('useConsentFlow must be used within ConsentFlowProvider');
  return ctx;
}
